export const RESERVED_SUBDOMAINS = [
  'admin',
  'api',
  'app',
  'auth',
  'billing',
  'cdn',
  'dashboard',
  'dev',
  'docs',
  'fisica',
  'help',
  'login',
  'mail',
  'master',
  'public',
  'register',
  'seven',
  'sevenanalytics',
  'sevenbpo',
  'staging',
  'static',
  'status',
  'support',
  'system',
  'terms',
  'test',
  'webhook',
  'www',
] as const

export const MAIN_DOMAINS = [
  'sevenbpo.com',
  'sevenbpo.com.br',
  'sevenanalytics.com',
  'sevenanalytics.com.br',
  'localhost',
  '127.0.0.1',
] as const

export interface TenantDomainResolution {
  isMainDomain: boolean
  isTenantSubdomain: boolean
  isCustomDomain: boolean
  slug?: string
  customDomain?: string
  detectedVia: 'subdomain' | 'custom_domain' | 'query_param' | 'main_domain'
}

/**
 * Normaliza e resolve o inquilino (tenant) com base no hostname e query params da requisição.
 *
 * Prioridades:
 * 1. Query Params (?tenant=slug ou ?subdomain=slug) para testes e ambiente de desenvolvimento.
 * 2. Subdomínio de localhost (ex: acme.localhost).
 * 3. Subdomínio em domínios oficiais da Seven (ex: acme.sevenbpo.com).
 * 4. Domínio customizado corporativo (ex: app.vanguardafinancas.com.br).
 * 5. Domínio principal / institucional (ex: app.sevenbpo.com, sevenbpo.com, localhost).
 */
export function resolveTenantFromHostname(
  rawHostname?: string,
  rawSearchParams?: URLSearchParams | string
): TenantDomainResolution {
  let hostname = rawHostname
  if (typeof hostname !== 'string' && typeof window !== 'undefined') {
    hostname = window.location.hostname
  }
  hostname = (hostname || '').toLowerCase().trim().replace(/:\d+$/, '')

  let params: URLSearchParams | null = null
  if (rawSearchParams instanceof URLSearchParams) {
    params = rawSearchParams
  } else if (typeof rawSearchParams === 'string') {
    params = new URLSearchParams(rawSearchParams)
  } else if (typeof window !== 'undefined') {
    params = new URLSearchParams(window.location.search)
  }

  // 1. Override via query param (?tenant=slug ou ?subdomain=slug)
  if (params) {
    const querySlug = (params.get('tenant') || params.get('subdomain') || '').toLowerCase().trim()
    if (querySlug) {
      if ((RESERVED_SUBDOMAINS as readonly string[]).includes(querySlug)) {
        return {
          isMainDomain: true,
          isTenantSubdomain: false,
          isCustomDomain: false,
          detectedVia: 'query_param',
        }
      }
      return {
        isMainDomain: false,
        isTenantSubdomain: true,
        isCustomDomain: false,
        slug: querySlug,
        detectedVia: 'query_param',
      }
    }
  }

  // 2. Localhost e IP puro
  if (hostname === 'localhost' || hostname === '127.0.0.1' || !hostname) {
    return {
      isMainDomain: true,
      isTenantSubdomain: false,
      isCustomDomain: false,
      detectedVia: 'main_domain',
    }
  }

  // 3. Subdomínio de localhost (ex: acme.localhost)
  if (hostname.endsWith('.localhost')) {
    const slug = hostname.slice(0, -'.localhost'.length)
    if (!slug || (RESERVED_SUBDOMAINS as readonly string[]).includes(slug)) {
      return {
        isMainDomain: true,
        isTenantSubdomain: false,
        isCustomDomain: false,
        detectedVia: 'main_domain',
      }
    }
    return {
      isMainDomain: false,
      isTenantSubdomain: true,
      isCustomDomain: false,
      slug,
      detectedVia: 'subdomain',
    }
  }

  // 4. Domínios oficiais da Seven (sevenbpo.com, sevenanalytics.com, etc.)
  const matchedBaseDomain = MAIN_DOMAINS.find(
    (d) => d !== 'localhost' && d !== '127.0.0.1' && (hostname === d || hostname.endsWith('.' + d))
  )

  if (matchedBaseDomain) {
    if (hostname === matchedBaseDomain || hostname === 'www.' + matchedBaseDomain) {
      return {
        isMainDomain: true,
        isTenantSubdomain: false,
        isCustomDomain: false,
        detectedVia: 'main_domain',
      }
    }

    const sub = hostname.slice(0, -(matchedBaseDomain.length + 1))
    const candidateSlug = sub.split('.')[0]

    if (!candidateSlug || (RESERVED_SUBDOMAINS as readonly string[]).includes(candidateSlug)) {
      return {
        isMainDomain: true,
        isTenantSubdomain: false,
        isCustomDomain: false,
        detectedVia: 'main_domain',
      }
    }

    return {
      isMainDomain: false,
      isTenantSubdomain: true,
      isCustomDomain: false,
      slug: candidateSlug,
      detectedVia: 'subdomain',
    }
  }

  // 5. Domínio customizado (FQDN próprio)
  return {
    isMainDomain: false,
    isTenantSubdomain: false,
    isCustomDomain: true,
    customDomain: hostname,
    detectedVia: 'custom_domain',
  }
}
